import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Loader2, Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage() {
  const { t, language } = useI18n();
  const { login, setUser, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);

  // Redirect if already authenticated
  React.useEffect(() => {

    if (isAuthenticated && !authLoading) {

      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  // Register form state
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirm, setRegisterConfirm] = useState('');
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterConfirm, setShowRegisterConfirm] = useState(false);
  const [registerNickname, setRegisterNickname] = useState(''); // Honeypot field

  // Verification state
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error(language === 'es' ? 'Por favor completa todos los campos' : 'Please fill in all fields');
      return;
    }

    setLoading(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s timeout

    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: loginEmail,
          password: loginPassword,
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      const data = await response.json();
      if (response.ok) {
        setUser(data);
        toast.success(language === 'es' ? '¡Bienvenido!' : 'Welcome!');
        navigate('/dashboard', { replace: true });
      } else if (response.status === 403 && data.detail === 'Unverified') {
        setRegisterEmail(loginEmail);
        setShowVerification(true);
        setActiveTab('register');
        toast.error(t('errors.unverified'));
      } else {
        toast.error(data.detail ? t(data.detail) : (language === 'es' ? 'Error al iniciar sesión' : 'Login error'));
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(
        language === 'es'
          ? `Error de conexión: ${error.message || 'Error desconocido'}`
          : `Connection error: ${error.message || 'Unknown error'}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!registerName || !registerEmail || !registerPassword || !registerConfirm) {
      toast.error(language === 'es' ? 'Por favor completa todos los campos' : 'Please fill in all fields');
      return;
    }

    if (registerPassword !== registerConfirm) {
      toast.error(language === 'es' ? 'Las contraseñas no coinciden' : 'Passwords do not match');
      return;
    }

    if (registerPassword.length < 6) {
      toast.error(language === 'es' ? 'La contraseña debe tener al menos 6 caracteres' : 'Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: registerName,
          email: registerEmail,
          password: registerPassword,
          nickname: registerNickname,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.require_verification) {
          setShowVerification(true);
          toast.success(language === 'es' ? 'Por favor verifica tu email' : 'Please verify your email');
        } else {
          setUser(data);
          toast.success(language === 'es' ? '¡Cuenta creada exitosamente!' : 'Account created successfully!');
          navigate('/dashboard', { replace: true });
        }
      } else {
        toast.error(data.detail ? t(data.detail) : (language === 'es' ? 'Error al registrarse' : 'Registration error'));
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(language === 'es' ? 'Error de conexión' : 'Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyRegistration = async (e) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.length < 6) return;

    setVerifying(true);
    try {
      const response = await fetch(`${API}/auth/verify-registration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: registerEmail,
          code: verificationCode
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setUser(data);
        toast.success(language === 'es' ? '¡Email verificado!' : 'Email verified!');
        navigate('/dashboard', { replace: true });
      } else {
        toast.error(data.detail || (language === 'es' ? 'Código inválido' : 'Invalid code'));
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error de conexión' : 'Connection error');
    } finally {
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: registerEmail }),
      });

      if (response.ok) {
        toast.success(t('errors.codeSent'));
      } else {
        const data = await response.json();
        toast.error(data.detail || t('errors.generic'));
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex">
      {/* Left side - Image (hidden on mobile) */}
      <div
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage:
            'url(/images/login_bg.jpg)',
        }}
      >
        <div className="absolute inset-0 bg-charcoal/30"></div>
        <div className="absolute bottom-12 left-12 right-12">
          <p className="font-accent text-4xl text-white rotate-[-2deg] drop-shadow-lg">
            {language === 'es'
              ? '"La mejor forma de planificar tus comidas"'
              : '"The best way to plan your meals"'}
          </p>
        </div>
      </div>

      {/* Right side - Auth forms */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <h1 className="font-manrope font-bold text-3xl text-charcoal mb-2">
              MealPlanner
            </h1>
            <div className="w-12 h-1 bg-primary mx-auto rounded-full"></div>
          </div>

          {/* Auth Card */}
          <div className="bg-white rounded-3xl shadow-soft p-6 md:p-8">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-muted/50 rounded-xl p-1">
                <TabsTrigger
                  value="login"
                  className="rounded-lg font-manrope data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  data-testid="login-tab"
                >
                  {language === 'es' ? 'Iniciar sesión' : 'Log in'}
                </TabsTrigger>
                <TabsTrigger
                  value="register"
                  className="rounded-lg font-manrope data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  data-testid="register-tab"
                >
                  {language === 'es' ? 'Registrarse' : 'Sign up'}
                </TabsTrigger>
              </TabsList>

              {/* Login Tab */}
              <TabsContent value="login" className="space-y-4">
                <form onSubmit={handleEmailLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="font-manrope text-charcoal">
                      Email
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone" strokeWidth={1.5} />
                      <Input
                        id="login-email"
                        type="email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        placeholder="tu@email.com"
                        className="pl-10 h-12 rounded-xl border-border bg-white/50 font-inter"
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="font-manrope text-charcoal">
                      {language === 'es' ? 'Contraseña' : 'Password'}
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone" strokeWidth={1.5} />
                      <Input
                        id="login-password"
                        type={showLoginPassword ? 'text' : 'password'}
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="••••••••"
                        className="pl-10 pr-10 h-12 rounded-xl border-border bg-white/50 font-inter"
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-stone hover:text-charcoal"
                        data-testid="login-password-toggle"
                      >
                        {showLoginPassword ? <EyeOff className="w-5 h-5" strokeWidth={1.5} /> : <Eye className="w-5 h-5" strokeWidth={1.5} />}
                      </button>
                    </div>
                  </div>

                  {/* Forgot Password Link */}
                  <div className="text-right">
                    <Link
                      to="/forgot-password"
                      className="text-sm text-primary hover:text-primary/80 font-inter transition-colors"
                      data-testid="forgot-password-link"
                    >
                      {language === 'es' ? '¿Olvidaste tu contraseña?' : 'Forgot your password?'}
                    </Link>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl h-12 font-manrope font-semibold"
                    data-testid="login-submit-button"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      language === 'es' ? 'Iniciar sesión' : 'Log in'
                    )}
                  </Button>
                </form>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-white px-4 text-stone font-inter">{t('or')}</span>
                  </div>
                </div>

                <Button
                  type="button"
                  onClick={login}
                  variant="outline"
                  className="w-full border-2 border-border text-charcoal hover:bg-muted rounded-xl h-12 font-manrope font-semibold flex items-center justify-center gap-3"
                  data-testid="google-login-button"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  {t('auth.continueWithGoogle')}
                </Button>
              </TabsContent>

              {/* Register Tab */}
              <TabsContent value="register" className="space-y-4">
                {showVerification ? (
                  <form onSubmit={handleVerifyRegistration} className="space-y-6 pt-4">
                    <div className="text-center space-y-2">
                      <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                        <Mail className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="font-manrope font-bold text-xl text-charcoal">
                        {language === 'es' ? 'Verifica tu email' : 'Verify your email'}
                      </h3>
                      <p className="text-stone text-sm">
                        {language === 'es'
                          ? `Hemos enviado un código a ${registerEmail}`
                          : `We sent a code to ${registerEmail}`}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="verification-code" className="font-manrope text-charcoal text-center block">
                        {language === 'es' ? 'Código de verificación' : 'Verification Code'}
                      </Label>
                      <Input
                        id="verification-code"
                        type="text"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.toUpperCase())}
                        placeholder="XXXXXX"
                        maxLength={6}
                        className="h-14 text-center text-2xl tracking-[0.5em] font-mono rounded-xl border-border bg-white/50"
                        autoFocus
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={verifying || verificationCode.length < 6}
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl h-12 font-manrope font-semibold"
                    >
                      {verifying ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        language === 'es' ? 'Verificar' : 'Verify'
                      )}
                    </Button>

                    <div className="text-center">
                      <button
                        type="button"
                        onClick={handleResendCode}
                        disabled={loading}
                        className="text-sm text-primary hover:text-primary/80 font-medium transition-colors"
                      >
                        {t('errors.resendCode')}
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowVerification(false)}
                      className="w-full text-sm text-stone hover:text-charcoal mt-4"
                    >
                      {language === 'es' ? 'Volver' : 'Back'}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name" className="font-manrope text-charcoal">
                        {language === 'es' ? 'Nombre' : 'Name'}
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone" strokeWidth={1.5} />
                        <Input
                          id="register-name"
                          type="text"
                          value={registerName}
                          onChange={(e) => setRegisterName(e.target.value)}
                          placeholder={language === 'es' ? 'Tu nombre' : 'Your name'}
                          className="pl-10 h-12 rounded-xl border-border bg-white/50 font-inter"
                          data-testid="register-name-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="register-email" className="font-manrope text-charcoal">
                        Email
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone" strokeWidth={1.5} />
                        <Input
                          id="register-email"
                          type="email"
                          value={registerEmail}
                          onChange={(e) => setRegisterEmail(e.target.value)}
                          placeholder="tu@email.com"
                          className="pl-10 h-12 rounded-xl border-border bg-white/50 font-inter"
                          data-testid="register-email-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="register-password" className="font-manrope text-charcoal">
                        {language === 'es' ? 'Contraseña' : 'Password'}
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone" strokeWidth={1.5} />
                        <Input
                          id="register-password"
                          type={showRegisterPassword ? 'text' : 'password'}
                          value={registerPassword}
                          onChange={(e) => setRegisterPassword(e.target.value)}
                          placeholder="••••••••"
                          className="pl-10 pr-10 h-12 rounded-xl border-border bg-white/50 font-inter"
                          data-testid="register-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-stone hover:text-charcoal"
                          data-testid="register-password-toggle"
                        >
                          {showRegisterPassword ? <EyeOff className="w-5 h-5" strokeWidth={1.5} /> : <Eye className="w-5 h-5" strokeWidth={1.5} />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="register-confirm" className="font-manrope text-charcoal">
                        {language === 'es' ? 'Confirmar contraseña' : 'Confirm password'}
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone" strokeWidth={1.5} />
                        <Input
                          id="register-confirm"
                          type={showRegisterConfirm ? 'text' : 'password'}
                          value={registerConfirm}
                          onChange={(e) => setRegisterConfirm(e.target.value)}
                          placeholder="••••••••"
                          className="pl-10 pr-10 h-12 rounded-xl border-border bg-white/50 font-inter"
                          data-testid="register-confirm-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowRegisterConfirm(!showRegisterConfirm)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-stone hover:text-charcoal"
                          data-testid="register-confirm-toggle"
                        >
                          {showRegisterConfirm ? <EyeOff className="w-5 h-5" strokeWidth={1.5} /> : <Eye className="w-5 h-5" strokeWidth={1.5} />}
                        </button>
                      </div>
                    </div>

                    {/* Honeypot field - hidden from users */}
                    <div className="sr-only" aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, height: 0, width: 0, overflow: 'hidden' }}>
                      <label htmlFor="register-nickname">Nickname</label>
                      <input
                        id="register-nickname"
                        type="text"
                        name="nickname"
                        tabIndex="-1"
                        autoComplete="off"
                        value={registerNickname}
                        onChange={(e) => setRegisterNickname(e.target.value)}
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl h-12 font-manrope font-semibold"
                      data-testid="register-submit-button"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        language === 'es' ? 'Crear cuenta' : 'Create account'
                      )}
                    </Button>
                  </form>
                )}

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-white px-4 text-stone font-inter">{t('or')}</span>
                  </div>
                </div>

                <Button
                  type="button"
                  onClick={login}
                  variant="outline"
                  className="w-full border-2 border-border text-charcoal hover:bg-muted rounded-xl h-12 font-manrope font-semibold flex items-center justify-center gap-3"
                  data-testid="google-register-button"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  {t('auth.continueWithGoogle')}
                </Button>
              </TabsContent>
            </Tabs>
          </div>

          {/* Back to home */}
          <div className="text-center mt-6 flex flex-col gap-2">
            <a
              href="/"
              className="font-inter text-stone hover:text-primary transition-colors"
              data-testid="back-to-home-link"
            >
              ← {t('back')} {t('nav.home').toLowerCase()}
            </a>
            <Link
              to="/privacy"
              className="font-inter text-xs text-stone/60 hover:text-primary transition-colors"
            >
              {language === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
